<template>
  <article class="static-partner container">
    <h1 v-html="h1" />
    <div v-html="content" />
    <LazyAboutPagePartners
      v-if="textAsync && textAsync.extra && textAsync.extra['providers-list']"
    />
  </article>
</template>

<script setup lang="ts">
import { useMainStore } from '~/store/main/main.store'
import { useTariffs } from '~/store/tariffs/tariffs.store'
import { textAggReplace } from '~/helpers'
import getTextAsync from '~/composible/getPageText'

const $route = useRoute()
const tariffsStore = useTariffs()
const mainStore = useMainStore()
const getCommonHeaders = computed(() => mainStore.getCommonHeaders)
const provider = computed(() => mainStore.getDomainConfig)
const getAddressText = computed(() => tariffsStore.getAddressText)

const h1 = computed(() =>
  textAsync.value?.h1
    ? textAggReplace(
      textAsync.value.h1,
      getAddressText.value,
    ).replace(/\\/g, '\n')
    : '',
)
const content = computed(() =>
  textAsync.value?.main_content
    ? textAggReplace(
      textAsync.value.main_content,
      getAddressText.value,
    ).replace(/\\/g, '\n')
    : '',
)

let errorStatus
let errorMessage
const { data: textAsync, error } = await useAsyncData(
  'textAsyncStatic' + $route.path,
  async () => {
    try {
      const res = await getTextAsync({ url: $route.path })

      mainStore.$patch({
        version: res.version,
      })
      return res
    }
    catch (e: any) {
      errorStatus = e.response.status
      errorMessage = e.message
      throw createError({
        statusCode: e.response.status,
        fatal: true,
        message: e.message,
      })
    }
    finally {
    }
  },
)

if (error.value) {
  throw createError({
    statusCode: errorStatus,
    fatal: true,
    message: errorMessage,
  })
}

useHead(() => ({
  title: () =>
    textAsync.value?.title
      ? textAggReplace(
        textAsync.value.title,
        getAddressText.value,
        undefined,
        provider.value?.provider.name,
      )
      : '',
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: () =>
        textAsync.value?.description
          ? textAggReplace(
            textAsync.value.description,
            getAddressText.value,
            undefined,
            provider.value?.provider.name,
          )
          : '',
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () =>
        textAggReplace(
          h1.value.replace(/-/g, '\u2011').replace(/\\/g, '\n'),
          getAddressText.value,
          undefined,
          provider.value?.provider.name,
        ),
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: provider.value
        ? `https://${provider.value?.domain}/android-icon-192x192.png`
        : 'https://dominternet.ru/android-icon-192x192.png',
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: () =>
        textAggReplace(
          textAsync.value.description,
          getAddressText.value,
          undefined,
          provider.value?.provider.name,
        ),
    },
  ],
}))
</script>

<style scoped lang="scss">
.static-partner {
  padding-top: 80px;
  padding-bottom: 80px;
  h1 {
    font-size: 50px;
    font-weight: 700;
    line-height: 1.4;
    margin-bottom: 64px;
    white-space: pre-wrap;

    @media (max-width: getBreakpoint(tablet)) {
      font-size: fontSize(headline-4);
    }
    @media (max-width: getBreakpoint(mobile-lg)) {
      font-size: 26px;
    }
    @media (max-width: getBreakpoint(mobile-md)) {
      font-size: fontSize(headline-5);
    }
  }
  p {
    white-space: pre-wrap;
    font-size: fontSize(headline-8);
    @media (max-width: getBreakpoint(tablet)) {
      font-size: fontSize(title);
    }
    @media (max-width: getBreakpoint(mobile-lg)) {
      font-size: fontSize(paragraph);
    }
  }
}
</style>
